import type { VodItem } from "@hybrid-iptv/types";
import { useParental } from "../settings/parentalStore.js";
import { useProfiles } from "../settings/profileStore.js";

/** The TMDB fields the hero shows. */
export interface KeptHeroTitle {
  id: number;
  title: string;
  overview?: string;
  backdrop_path?: string | null;
  /** An Open hero's picture (Section 26): the provider's own URL, where TMDB's hero has a path. */
  backdropUrl?: string;
  release_date?: string | null;
  vote_average?: number;
}

/**
 * The hero Home last showed, kept on the device with what it takes to play it,
 * so a cold start opens on a real hero at once. WHY (projector, 2026-09-26):
 * the live hero needs a catalogue lookup that waits behind the startup work,
 * about 22 s into a cold start, so the launch overlay either waited for it or
 * lifted onto the placeholder. Used only for the same source, profile and
 * parental settings it was kept under — a new lock or a kids profile waits for
 * the live hero, which the catalogue lookup filters. The live hero replaces it
 * as soon as it arrives.
 */
export const KEPT_HERO_KEY = "slq.homeHero";

export interface KeptHero<T extends KeptHeroTitle = KeptHeroTitle> {
  scope: string;
  title: T;
  item: VodItem;
}

export function heroScope(sourceId: string): string {
  const parental = useParental.getState();
  const profiles = useProfiles.getState();
  const profile = profiles.profiles.find((p) => p.id === profiles.activeProfileId);
  return JSON.stringify([sourceId, profiles.activeProfileId, profile?.isKids === true, parental.pinHash, parental.autoLockAdult, [...parental.lockedCategoryIds].sort()]);
}

export function readKeptHero<T extends KeptHeroTitle>(sourceId: string): KeptHero<T> | undefined {
  try {
    const kept = JSON.parse(localStorage.getItem(KEPT_HERO_KEY) ?? "null") as KeptHero<T> | null;
    // An Open hero has the provider's backdropUrl and no TMDB path: it was refused here, so a cold
    // start under Open always waited for the live hero (seen 2026-10-12, onn box: placeholder for 11 s).
    if (!kept?.title?.id || !(kept.title.backdrop_path || kept.title.backdropUrl) || !kept.item?.id) return undefined;
    return kept.scope === heroScope(sourceId) ? kept : undefined;
  } catch {
    return undefined;
  }
}

